import React from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Image, Card, Button } from "antd";
import { useMediaQuery } from "react-responsive";
import { motion } from "framer-motion";
import styled from "styled-components";
import { BsArrowRight } from "react-icons/bs";
import { FilePdfOutlined, DownloadOutlined } from "@ant-design/icons";

import { HeroImage } from "../assets/images";
import { VisionCard } from "../component/Card";
import { B1, B3, H1, H2, H4 } from "../component/Typography";
import { DarkButton } from "../component/Button";
import { visionItems } from "../data/visionData";
import { useAnimatedInView } from "../hook/useAnimationInView";
import { ConnectIcon } from "../assets/icons";

// 홈에 노출할 자료 다운로드 목록.
// 새 자료 추가 시 이 배열에만 항목 추가하면 됨. 파일은 public/files/ 에 둘 것.
const WORKSHOP_MATERIALS = [
  {
    file: "KLA_workshop_2026-06-13_배소영_자발적발화의임상적가치.pdf",
    title: "Beyond scores: 자발적 발화 분석의 임상적 가치",
    presenter: "배소영 교수 (한림대학교 언어청각학부)",
  },
  {
    file: "KLA_workshop_2026-06-13_하승희_KLA활용법.pdf",
    title: "데이터로 말하는 말-언어발달: KLA 활용법",
    presenter: "하승희 교수 (한림대학교 언어청각학부)",
  },
];

const Home = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width: 576px)" });
  const [ref, controls] = useAnimatedInView(0.1);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } },
  };

  return (
    <div>
      {/* Hero Section (텍스트 + 버튼) */}
      <HeroSection
        isMobile={isMobile}
        fadeIn={fadeIn}
        fadeInUp={fadeInUp}
        navigate={navigate}
      />

      {/* Workshop Materials Download Section
          (작은 화면에서도 첫 화면에 노출되도록 둥글둥글 이미지 위에 배치) */}
      <WorkshopMaterialsSection isMobile={isMobile} />

      {/* Hero 이미지 (둥글둥글한 배경 그래픽) - 워크숍 자료 아래로 이동
          원래 Hero 안에 있던 것을 그대로 옮김. styled(Image)로 감싸면 CSS가
          wrapper div에 적용되어 폭이 줄어들므로 inline style로 유지. */}
      <div style={{ margin: "32px 0" }}>
        <Image
          preview={false}
          src={HeroImage}
          style={{ width: "100vw", height: "auto", objectFit: "cover" }}
        />
      </div>

      {/* Vision Section */}
      <VisionSection ref={ref} isMobile={isMobile} visionItems={visionItems} />
    </div>
  );
};

// Hero Section: 타이틀 + 부제 + "서비스 시작하기" 버튼만.
// 둥글둥글 배경 이미지는 워크숍 자료 섹션 아래로 분리됨.
const HeroSection = ({ isMobile, fadeIn, fadeInUp, navigate }) => (
  <HeroContainer
    as={motion.div}
    initial="hidden"
    animate="visible"
    isMobile={isMobile}
    variants={fadeIn}
  >
    <HeroWrapper as={motion.div} variants={fadeInUp}>
      <H1>모두의 말소리, Speech4All</H1>
      <SubText>
        Speech4All은 AI 음성인식 기술을 기반으로하는 말소리와 발화 분석 전문
        회사입니다.
      </SubText>
      <DarkButton
        type="primary"
        size="large"
        iconPosition="end"
        icon={
          <BsArrowRight
            size="24px"
            style={{ marginLeft: "4px", color: "white" }}
          />
        }
        onClick={() => navigate("/kla/intro")}
      >
        서비스 시작하기
      </DarkButton>
    </HeroWrapper>
  </HeroContainer>
);

// 워크숍 자료 다운로드 섹션
// 서비스 시작하기 버튼 바로 아래에 노출. 작은 화면에서도 첫 화면에 보이도록 배치.
const WorkshopMaterialsSection = ({ isMobile }) => (
  <MaterialsContainer isMobile={isMobile}>
    <MaterialsHeader>
      <H2>📚 KLA 워크숍 자료</H2>
    </MaterialsHeader>
    <Row gutter={[24, 24]}>
      {WORKSHOP_MATERIALS.map((m) => (
        <Col xs={24} md={12} key={m.file}>
          <MaterialCard hoverable>
            <CardInner>
              <FilePdfOutlined style={{ fontSize: 42, color: "#d4380d", flexShrink: 0 }} />
              <CardBody>
                <H4 style={{ margin: 0 }}>{m.title}</H4>
                <PresenterText>{m.presenter}</PresenterText>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  href={`/files/${m.file}`}
                  download={m.file}
                  size="large"
                  style={{ marginTop: 8, alignSelf: "flex-start" }}
                >
                  PDF 다운로드
                </Button>
              </CardBody>
            </CardInner>
          </MaterialCard>
        </Col>
      ))}
    </Row>
  </MaterialsContainer>
);

// Vision Section as a separate component
const VisionSection = React.forwardRef(({ isMobile, visionItems }, ref) => (
  <VisionContainer ref={ref} isMobile={isMobile}>
    <div style={{ textAlign: "center" }}>
      <Image
        preview={false}
        src={ConnectIcon}
        style={{ width: "128px", height: "128px" }}
      />
      <H2 style={{ fontWeight: "normal" }}>추구하는 가치와 방향</H2>
      <H2>Speech4All의 핵심가치</H2>
    </div>
    <Row gutter={[32, 32]}>
      {visionItems.map((item, index) => (
        <VisionCardItem
          isMobile={isMobile}
          key={index}
          {...item}
          delay={index * 0.2}
        />
      ))}
    </Row>
  </VisionContainer>
));

// VisionCardItem component for individual vision cards
const VisionCardItem = ({
  isMobile,
  imageSource,
  subtitle,
  title,
  description,
  delay,
}) => {
  const [ref, controls] = useAnimatedInView(0.2);
  const [hover, setHover] = React.useState(false);

  const cardAnimation = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
  };

  return (
    <Col
      xs={24}
      md={12}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <motion.div
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={cardAnimation}
        style={{ height: "100%", position: "relative" }}
      >
        <Image
          preview={false}
          src={imageSource}
          style={{
            width: "100%",
            borderRadius: "20px",
            opacity: hover ? 0.2 : 0.8,
            transition: "opacity 0.3s ease",
          }}
        />

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            padding: "24px 36px",
            lineHeight: 1.8,
            fontSize: isMobile ? "12px" : "16px",
            opacity: hover ? 1 : 0,
            transform: hover ? "translateY(0)" : "translateY(10px)",
            transition: "opacity 0.3s ease, transform 0.3s ease",
          }}
        >
          {description.map((text, idx) => (
            <li key={idx}>{text}</li>
          ))}
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            opacity: hover ? 1 : 0.8,
          }}
        >
          <B1>{subtitle}</B1>
          <H1
            style={{
              WebkitTextStroke: "2px black",
              textTransform: "uppercase",
              letterSpacing: "2px",
            }}
          >
            {title}
          </H1>
        </div>
      </motion.div>
    </Col>
  );
};

// Styled Components for layout and styling
const HeroContainer = styled.div`
  padding: ${({ isMobile }) =>
    isMobile ? "48px 0 24px" : "96px 0 32px"};
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
`;

const HeroWrapper = styled.div`
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  text-align: center;
`;

const VisionContainer = styled.div`
  padding: ${({ isMobile }) => (isMobile ? "64px 24px" : "128px 72px")};
  gap: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  max-width: 1280px;
  margin: 0 auto;
`;

const SubText = styled.div`
  color: var(--text-secondary);
  text-align: center;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 24px;
`;

const MaterialsContainer = styled.div`
  padding: ${({ isMobile }) => (isMobile ? "32px 24px" : "48px 72px")};
  background-color: var(--bg-light, #f7f9fc);
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const MaterialsHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const MaterialCard = styled(Card)`
  height: 100%;
  border-radius: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }
`;

const CardInner = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const PresenterText = styled.div`
  color: var(--text-secondary, #666);
  font-size: 13px;
`;

const DescriptionText = styled.div`
  color: var(--text-tertiary, #888);
  font-size: 13px;
  line-height: 1.6;
  margin-top: 4px;
`;

export default Home;
